import { http, normalizePage } from '@shared/api';
import type { Page, UUID } from '@shared/api';
import type { MediaLink } from '@entities/media';
import type {
  CreateProducerRequest,
  ManufacturerFilters,
  ManufacturerListItem,
  ManufacturerProfile,
  SetPasswordRequest,
  UpdateProfileRequest,
} from './types';

function toSearchParams(filters: ManufacturerFilters): Record<string, string | number> {
  const params: Record<string, string | number> = {
    page: filters.page ?? 0,
    size: filters.size ?? 20,
  };
  if (filters.kyc?.length) params.kyc = filters.kyc.join(',');
  if (filters.country) params.country = filters.country;
  if (filters.eco != null) params.eco = String(filters.eco);
  if (filters.q) params.q = filters.q;
  return params;
}

interface RawListItem {
  id: UUID;
  companyName: string;
  country: string;
  categories?: UUID[];
  categoryIds?: UUID[];
  kycStatus: ManufacturerListItem['kycStatus'];
  ecoProducer: boolean;
  active: boolean;
  updatedAt?: string | null;
}

interface RawProfile extends RawListItem {
  description?: string | null;
  website?: string | null;
  foundedYear?: number | null;
  employeesRange?: string | null;
  logo?: MediaLink | null;
  video?: MediaLink | null;
  certificates?: MediaLink[];
  ecoCertificates?: MediaLink[];
  kycRejectionReason?: string | null;
}

const mapListItem = (r: RawListItem): ManufacturerListItem => ({
  id: r.id,
  companyName: r.companyName,
  country: r.country,
  categoryIds: r.categoryIds ?? r.categories ?? [],
  kycStatus: r.kycStatus,
  ecoProducer: r.ecoProducer,
  active: r.active,
  updatedAt: r.updatedAt ?? null,
});

const mapProfile = (r: RawProfile): ManufacturerProfile => ({
  ...mapListItem(r),
  description: r.description ?? null,
  website: r.website ?? null,
  foundedYear: r.foundedYear ?? null,
  employeesRange: r.employeesRange ?? null,
  logo: r.logo ?? null,
  video: r.video ?? null,
  certificates: r.certificates ?? [],
  ecoCertificates: r.ecoCertificates ?? [],
  kycRejectionReason: r.kycRejectionReason ?? null,
});

export const manufacturerApi = {
  list: async (filters: ManufacturerFilters): Promise<Page<ManufacturerListItem>> => {
    const raw = await http
      .get('manufacturers', { searchParams: toSearchParams(filters) })
      .json<unknown>();
    return normalizePage<RawListItem, ManufacturerListItem>(raw, mapListItem);
  },

  get: async (id: UUID): Promise<ManufacturerProfile> => {
    const raw = await http.get(`manufacturers/${id}`).json<RawProfile>();
    return mapProfile(raw);
  },

  approveKyc: (id: UUID) => http.post(`manufacturers/${id}/kyc/approve`).json<void>(),

  rejectKyc: (id: UUID, reason: string) =>
    http.post(`manufacturers/${id}/kyc/reject`, { json: { reason } }).json<void>(),

  create: async (body: CreateProducerRequest): Promise<ManufacturerProfile> => {
    const raw = await http.post('manufacturers', { json: body }).json<RawProfile>();
    return mapProfile(raw);
  },

  updateProfile: async (id: UUID, body: UpdateProfileRequest): Promise<ManufacturerProfile> => {
    const raw = await http.put(`manufacturers/${id}/profile`, { json: body }).json<RawProfile>();
    return mapProfile(raw);
  },

  setPassword: (id: UUID, body: SetPasswordRequest) =>
    http.post(`manufacturers/${id}/password`, { json: body }).json<void>(),
};
